import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Properties } from "posthog-js";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Fade from "@mui/material/Fade";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import QrCard from "./QrCard";
import QrCode from "./QrCode";

import StyledModal from "components/Modal";

import { isIOS } from "react-device-detect";

import { useQrDownloader } from "hooks/useQrDownloader";
import { shareOrDownload } from "shared/functions/imageHelpers";
import { QR_ELEMENT_ID, TabId, qrTabs } from "./constants";
import { IbanInvoice } from "store/features/ibanInvoiceSlice";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { styles } from "./styles";

interface InvoiceQRCodeModalProps {
  isOpen: boolean;
  invoice: IbanInvoice | null;
  onClose: () => void;
}

type DownloadFormat = "png" | "svg";

const InvoiceQRCodeModal: React.FC<InvoiceQRCodeModalProps> = (props) => {
  const { isOpen, invoice, onClose } = props;

  const { downloadQrAsPng, downloadQrAsSvg } = useQrDownloader();
  const cardRef = useRef<HTMLDivElement>(null);

  const [visibleTab, setVisibleTab] = useState<string>(qrTabs[0].id);
  const [format, setFormat] = useState<DownloadFormat>("png");

  const baseName = `ibanoplata-qr-${new Date().toISOString().slice(0, 10)}`;

  const invoiceUrl = invoice?.ibanInvoiceUrl || "";
  const name = invoice?.publicName || invoice?.organizationName;

  const sendAnalytics = (properties?: Properties | null) =>
    analytics().sendConversionEvent(
      AnalyticsEvents.qrcode_invoice_install_ + "table_modal",
      properties,
      true,
    );

  const handleCardDownload = async () => {
    if (!invoiceUrl || !cardRef.current) return;

    const dataUrl = await toPng(cardRef.current, {
      style: { borderRadius: "0" },
    });

    if (isIOS) {
      const blob = await fetch(dataUrl).then((r) => r.blob());
      await shareOrDownload(blob, `${baseName}.png`);
      return;
    }

    const link = document.createElement("a");
    link.download = `${baseName}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleQrDownload = async () => {
    if (!invoiceUrl) return;

    sendAnalytics({ format });

    if (format === "svg") {
      await downloadQrAsSvg(QR_ELEMENT_ID, `${baseName}.svg`);
    } else {
      await downloadQrAsPng(QR_ELEMENT_ID, `${baseName}.png`);
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      sx={{ maxWidth: "480px" }}
      title="QR-код для оплати за реквізитами"
      subtitle="Завантажте QR-код або дайте клієнту відсканувати його для оплати."
      onAction={
        visibleTab === TabId.Card ? handleCardDownload : handleQrDownload
      }
      actionBtnText="Завантажити QR-код"
      content={
        <Box sx={styles.content}>
          <Tabs value={visibleTab} onChange={(_, val) => setVisibleTab(val)}>
            {qrTabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                sx={styles.tab}
                data-testid={`subs-tab-${tab.id}`}
              />
            ))}
          </Tabs>

          <Box sx={{ width: "100%" }}>
            {visibleTab === TabId.Card && (
              <Fade in>
                <Box sx={styles.cardWrapper}>
                  <QrCard ref={cardRef} invoiceUrl={invoiceUrl} name={name} />
                </Box>
              </Fade>
            )}
            {visibleTab === TabId.QrCode && (
              <Fade in>
                <Box>
                  <QrCode invoiceUrl={invoiceUrl} />
                  <Typography variant="subtitle2" color="grey800" mb={1} mt={3}>
                    Формат файлу
                  </Typography>
                  <Select
                    size="small"
                    value={format}
                    fullWidth
                    onChange={(e) =>
                      setFormat(e.target.value as DownloadFormat)
                    }
                    MenuProps={{ PaperProps: { sx: { p: "0 !important" } } }}
                  >
                    <MenuItem value="png">PNG</MenuItem>
                    <MenuItem value="svg">SVG</MenuItem>
                  </Select>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default InvoiceQRCodeModal;
